import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import { useNavigate } from "react-router-dom";

const FaceVerify = () => {
  const webcamRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const [isMobile, setIsMobile] = useState(false);

    const navigate = useNavigate();

    const redirectToPayment = (user) => {
    navigate(`/topup?user=${user.id}&isFaceVerified=true`);
    };
  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0;

    const isSmallScreen = window.innerWidth <= 768;

    setIsMobile(isTouchDevice && isSmallScreen);
  }, []);

  const captureAndVerify = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setError("Camera access required to continue");
      setLoading(false);
      return;
    }

    const blob = await fetch(imageSrc).then((res) => res.blob());
    const file = new File([blob], "face.jpg", { type: "image/jpeg" });

    const formData = new FormData();
    formData.append("image", file);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("adminToken");

    if (!token) {
      setError("Session expired. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/face-verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok) {
        setError(data.message || "Face verification failed");
      } else {
        setSuccess("Face verified successfully");
        setUserData(data.user);     // 👈 store user data
        setShowModal(true);         // 👈 open popup
      }
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleCamera = () => {
    setFacingMode((prev) =>
      prev === "user" ? "environment" : "user"
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Verify Your Identity</h2>
        <p style={styles.desc}>
          We need to verify your face before proceeding with the payment
        </p>

        <div style={styles.cameraBox}>
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            // videoConstraints={{ facingMode: "user" }}
            videoConstraints={{
              facingMode: facingMode,
              width: 720,
              height: 720,
            }}
            style={styles.webcam}
          />
          

          {loading && (
            <div style={styles.loader}>
              <div style={styles.spinner}></div>
              <span>Verifying…</span>
            </div>
          )}
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        {isMobile && (
          <button
            onClick={toggleCamera}
            disabled={loading}
            style={{
              ...styles.button,
              background: "#0ea5e9",
              marginTop: 12,
            }}
          >
            Switch Camera
          </button>
        )}

        <button
          onClick={captureAndVerify}
          disabled={loading || success}
          style={{
            ...styles.button,
            opacity: loading || success ? 0.6 : 1,
          }}
        >
          {loading ? "Verifying…" : success ? "Verified" : "Verify Face"}
        </button>

        <p style={styles.footerText}>
          Make sure your face is clearly visible and well-lit
        </p>
      </div>

      {/* 🔔 USER DETAILS MODAL */}
      {showModal && userData && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Face Verified</h3>

            <div style={styles.userRow}>
              <span>Name</span>
              <strong>{userData.name}</strong>
            </div>

            <div style={styles.userRow}>
              <span>Email</span>
              <strong>{userData.email}</strong>
            </div>

            <div style={styles.userRow}>
              <span>Contact</span>
              <strong>{userData.contact_number}</strong>
            </div>

            <button
                style={{
                    ...styles.button,
                    opacity: loading || success ? 0.6 : 1,
                }}
              onClick={() => { setShowModal(false); redirectToPayment(userData); }}
            >
              Continue Payment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* 🎨 STYLES */
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
    padding: 16,
  },
  card: {
    width: 380,
    background: "#fff",
    borderRadius: 16,
    padding: "28px 22px",
    boxShadow: "0 25px 60px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    color: "#0f172a",
  },
  desc: {
    fontSize: 14,
    color: "#64748b",
    margin: "10px 0 24px",
  },
  cameraBox: {
    position: "relative",
    width: 240,
    height: 240,
    margin: "0 auto 18px",
    borderRadius: "50%",
    overflow: "hidden",
    border: "3px solid #6366f1",
    background: "#000",
  },
  webcam: { width: "100%", height: "100%", objectFit: "cover" },
  loader: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "4px solid rgba(255,255,255,0.3)",
    borderTop: "4px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 8,
  },
  button: {
    width: "100%",
    padding: 14,
    borderRadius: 10,
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    fontSize: 16,
    fontWeight: 600,
    marginTop: 8,
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: 10,
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 10,
  },
  successBox: {
    background: "#dcfce7",
    color: "#166534",
    padding: 10,
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 10,
  },
  footerText: {
    fontSize: 12,
    color: "#94a3b8",
    marginTop: 14,
  },

  /* MODAL */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    width: 320,
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    textAlign: "center",
  },
  modalTitle: {
    marginBottom: 16,
    color: "#0f172a",
  },
  userRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
    marginBottom: 10,
  },
  modalBtn: {
    marginTop: 14,
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "none",
    background: "#22c55e",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default FaceVerify;
